"use client";

// Third-party Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Type Imports
import type { entityLoggingTypes } from "@/types/apps/EntityLogging";

// Server Imports
import { doGetEntityGeneralLogsManage } from "@/app/server/actions";

export interface LogFetchParams {
  from?: number;
  size?: number;
  start?: string;
  end?: string;
  search?: string;
  logtype?: string;
  status?: string;
}

const initialState: entityLoggingTypes = {
  generalLogs: [],
  filteredGeneralLogs: [],
  selectedGeneralTypes: ["success", "error"],
  loading: "idle",
  error: null,
  total: 0,
  lastErrorToastTime: 0,
};

export const fetchGeneralLogs = createAsyncThunk<any, LogFetchParams | undefined, { rejectValue: string }>(
  "manage_logging/fetch_general_logs",
  async (params, { rejectWithValue }) => {
    try {
      const response = await doGetEntityGeneralLogsManage(params);

      if (response === null || response === undefined) {
        return rejectWithValue("Failed to load logs");
      }

      // API now returns { hits, total, from, size }
      if (response && typeof response === "object" && "hits" in response) {
        const r = response as { hits: unknown; total: unknown; from: unknown; size: unknown };

        
return {
          data: Array.isArray(r.hits) ? r.hits : [],
          total: typeof r.total === "number" ? r.total : 0,
          from: typeof r.from === "number" ? r.from : 0,
          size: typeof r.size === "number" ? r.size : 50,
        };
      }

      // Fallback: legacy array response
      const logs = Array.isArray(response) ? response : [];

      
return { data: logs, total: logs.length, from: 0, size: 50 };
    } catch (err) {
      return rejectWithValue("Failed to load logs");
    }
  }
);

export const entityManageLoggingSlice = createSlice({
  name: "manageLogging",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchGeneralLogs.pending, state => {
      state.loading = "pending";
      state.error = null;
    });
    builder.addCase(fetchGeneralLogs.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.loading = "idle";
        
return;
      }

      state.loading = "failed";
      state.error = action.payload ?? "Unknown error";

      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load logs. Please try again.", { toastId: "logs-load-error" });
      }
    });
    builder.addCase(fetchGeneralLogs.fulfilled, (state, action) => {
      state.generalLogs = action.payload.data;
      state.filteredGeneralLogs = action.payload.data;
      state.total = action.payload.total;
      state.loading = "succeeded";
    });
  },
});

export default entityManageLoggingSlice.reducer;
